// NOTE: this import must stay types-only in effect (values are never referenced below).
// Babel elides it entirely, so this file never pulls in the MSAL module chain that
// crashes Login.test.tsx under jsdom. Do not import any runtime value here.
import {
    ImeasurementPoint,
    ImeasurementPointList,
    ImeasurementPointListSummary,
    ImeasurementPointListTab
} from '../../models';

/*
 * Pure logic behind "copy this tab to another measurement point list".
 * No React, no redux - so it can be unit tested directly.
 */

export type IcopyTabTargetField = 'mainCategoryID' | 'type' | 'standardID';

export interface IcopyTabTargetSelection {
    mainCategoryID?: string;
    type?: number;
    standardID?: string;
}

/*
 * A tab can never be copied onto its own list, so the source is never a candidate.
 */
const candidateSummaries = (
    summaries: ImeasurementPointListSummary[],
    sourceMeasurementPointListID: string
) => summaries.filter(summary => summary.id !== sourceMeasurementPointListID);

/*
 * Does this summary satisfy the selection, ignoring one field?
 * Ignoring the field being computed is what makes the cascade order independent -
 * a field's own current value never narrows its own option list.
 *
 * `type` is compared with `!== undefined` rather than truthiness, unlike the two ID
 * fields: it is numeric, and a falsy-but-set value (0) must still count as "selected".
 * measurementPointListTypeEnum currently starts at 1, so no real value hits this today
 * - the check is defensive against a future enum member equal to 0, not a live bug.
 */
const matchesSelectionExcept = (
    summary: ImeasurementPointListSummary,
    selection: IcopyTabTargetSelection,
    ignore: IcopyTabTargetField
): boolean => {
    if (
        ignore !== 'mainCategoryID' &&
        selection.mainCategoryID &&
        summary.mainCategoryID !== selection.mainCategoryID
    ) {
        return false;
    }
    if (
        ignore !== 'type' &&
        selection.type !== undefined &&
        summary.type !== selection.type
    ) {
        return false;
    }
    if (
        ignore !== 'standardID' &&
        selection.standardID &&
        summary.standardID !== selection.standardID
    ) {
        return false;
    }
    return true;
};

/*
 * The values one dropdown may offer, given whatever the other two are set to.
 *
 * Overloaded on the literal field name so a call site with a known field (the common
 * case - populating one specific dropdown) gets back a properly typed string[] or
 * number[] instead of the union, matching the Ioption / IoptionNumberValue split used
 * elsewhere in this codebase. The third, wider signature exists for call sites (like
 * pruneInvalidatedSelection below) that iterate over IcopyTabTargetField generically.
 */
export function availableValuesFor(
    field: 'type',
    summaries: ImeasurementPointListSummary[],
    sourceMeasurementPointListID: string,
    selection: IcopyTabTargetSelection
): number[];
export function availableValuesFor(
    field: 'mainCategoryID' | 'standardID',
    summaries: ImeasurementPointListSummary[],
    sourceMeasurementPointListID: string,
    selection: IcopyTabTargetSelection
): string[];
export function availableValuesFor(
    field: IcopyTabTargetField,
    summaries: ImeasurementPointListSummary[],
    sourceMeasurementPointListID: string,
    selection: IcopyTabTargetSelection
): Array<string | number>;
export function availableValuesFor(
    field: IcopyTabTargetField,
    summaries: ImeasurementPointListSummary[],
    sourceMeasurementPointListID: string,
    selection: IcopyTabTargetSelection
): Array<string | number> {
    const matching = candidateSummaries(
        summaries,
        sourceMeasurementPointListID
    ).filter(summary => matchesSelectionExcept(summary, selection, field));

    return Array.from(new Set(matching.map(summary => summary[field])));
}

/*
 * The destination list, or null when the selection is incomplete or does not
 * identify exactly one list.
 */
export const resolveTargetMeasurementPointListID = (
    summaries: ImeasurementPointListSummary[],
    sourceMeasurementPointListID: string,
    selection: IcopyTabTargetSelection
): string | null => {
    if (
        !selection.mainCategoryID ||
        selection.type === undefined ||
        !selection.standardID
    ) {
        return null;
    }

    const matches = candidateSummaries(
        summaries,
        sourceMeasurementPointListID
    ).filter(
        summary =>
            summary.mainCategoryID === selection.mainCategoryID &&
            summary.type === selection.type &&
            summary.standardID === selection.standardID
    );

    return matches.length === 1 ? matches[0].id : null;
};

/*
 * Order in which pruneInvalidatedSelection re-checks fields when the selection and
 * summaries have drifted apart without a user click (see that function for why user
 * clicks alone can never cause this). `type` is checked first because it is the most
 * derived of the three axes - which test types apply is a function of equipment
 * category and standard, not the reverse.
 */
const PRUNE_FIELD_ORDER: IcopyTabTargetField[] = [
    'type',
    'mainCategoryID',
    'standardID'
];

/*
 * Each dropdown's options are computed as the values of that field among summaries
 * that already match the other two selected fields (see availableValuesFor). So
 * whatever value the user picks, a matching summary necessarily exists, and clearing
 * a field only drops a constraint - a subset of a matching triple still matches.
 * User clicks can therefore never produce an invalid selection on their own.
 *
 * What this actually guards against is the selection and `summaries` drifting apart
 * *without* a user click: a background refetch while the modal is open (someone else
 * edits or deletes the current target list), or `sourceMeasurementPointListID`
 * changing so candidateSummaries admits/excludes different rows. Since no user
 * choice caused that kind of conflict, no field has a stronger claim to survive over
 * another, which is why the fixed PRUNE_FIELD_ORDER pass order is an acceptable way
 * to resolve it rather than an arbitrary one.
 *
 * The `while (changedThisPass)` loop has no hardcoded round cap; it self-bounds
 * because each productive pass deletes at least one of the three fields and
 * deletions are never re-added. When a pass makes no deletions, every surviving
 * field's value has just been confirmed against the other survivors, so the loop
 * can never exit leaving an unmatched combination.
 */
export const pruneInvalidatedSelection = (
    summaries: ImeasurementPointListSummary[],
    sourceMeasurementPointListID: string,
    selection: IcopyTabTargetSelection
): IcopyTabTargetSelection => {
    const pruned: IcopyTabTargetSelection = { ...selection };

    let changedThisPass = true;
    while (changedThisPass) {
        changedThisPass = false;

        PRUNE_FIELD_ORDER.forEach(field => {
            const currentValue = pruned[field];
            if (currentValue === undefined) {
                return;
            }

            const validValues = availableValuesFor(
                field,
                summaries,
                sourceMeasurementPointListID,
                pruned
            );

            if (!validValues.includes(currentValue)) {
                delete pruned[field];
                changedThisPass = true;
            }
        });
    }

    return pruned;
};

/*
 * Rebuild a measurement point with its own keys sorted. Two point objects with
 * identical field values but a different key insertion order (e.g. one spread from
 * an existing object, one built fresh) must compare equal - JSON.stringify does not
 * do that on its own, since it serializes keys in insertion order.
 */
const canonicalPoint = (point: ImeasurementPoint): ImeasurementPoint => {
    const sorted: Record<string, unknown> = {};
    Object.keys(point)
        .sort()
        .forEach(key => {
            sorted[key] = ((point as unknown) as Record<string, unknown>)[key];
        });
    return (sorted as unknown) as ImeasurementPoint;
};

/*
 * Compare on a canonical shape rather than the raw objects: measurementPoints is a
 * keyed map, so its insertion order can differ between the saved copy and the edited
 * copy even when the content is identical. Sorting the points by id removes that
 * false positive at the map level; canonicalPoint removes the same false positive
 * inside each individual point.
 */
const canonicalTab = (tab: ImeasurementPointListTab) => ({
    name: tab.name,
    order: tab.order,
    isDeleted: tab.isDeleted,
    subCategoryID: tab.subCategoryID || null,
    measurementPoints: Object.keys(tab.measurementPoints)
        .sort()
        .map((key: string) =>
            canonicalPoint(tab.measurementPoints[key] as ImeasurementPoint)
        )
});

/*
 * The copy endpoint reads the SAVED tab, so unsaved edits must block the copy.
 * savedList comes from measurementPointListsByID (last-saved truth); selectedList is
 * the in-modal copy that absorbs edits.
 */
export const isSelectedTabDirty = (
    savedList: ImeasurementPointList | undefined,
    selectedList: ImeasurementPointList,
    selectedTabID: string
): boolean => {
    if (!savedList) {
        return true;
    }

    const savedTab = savedList.measurementPointTabs.find(
        tab => tab.id === selectedTabID
    );
    const currentTab = selectedList.measurementPointTabs.find(
        tab => tab.id === selectedTabID
    );

    if (!savedTab || !currentTab) {
        return true;
    }

    return (
        JSON.stringify(canonicalTab(savedTab)) !==
        JSON.stringify(canonicalTab(currentTab))
    );
};
